/**
 * Configuration data models.
 */

import { BaseEntity, TimestampMixin } from './base';

/** How this organization syncs with Profit Plus 2k8 (ERP). */
export type ProfitPlus2k8SyncType =
  | 'none'           // No sync (default when disabled)
  | 'import_only'    // SQL → Firebase only (read from ERP)
  | 'export_only'    // Firebase → SQL only (write to ERP)
  | 'bidirectional'; // Two-way sync

/** Inventory scope: single warehouse (default) or multi-warehouse balances. */
export type InventoryMode = 'single' | 'multi';

/** Organization-level Profit Plus 2k8 integration (admin enables and chooses sync type). */
export interface ProfitPlus2k8OrgConfig {
  /** Whether the organization uses Profit Plus 2k8 integration. */
  enabled: boolean;
  /** Type of synchronization with the ERP. */
  syncType: ProfitPlus2k8SyncType;
  /**
   * When true, accepted sales (orders) in the admin are synced to Profit Plus as new pedidos.
   * Only applies when syncType is 'export_only' or 'bidirectional'. The bridge creates
   * dbo.pedidos + dbo.reng_ped when an order is approved.
   */
  syncAcceptedSalesToProfit?: boolean;
  /**
   * When true, ask the admin on approval whether to create the Profit pedido.
   * If false, approved sales follow syncAcceptedSalesToProfit automatically.
   */
  askBeforeCreatingProfitPedido?: boolean;
  /**
   * When true, MayorX owns product availability even if Profit Plus is enabled.
   * Profit inventory refresh/import flows must not overwrite product.availability.
   */
  stockManagedInMayorX?: boolean;
  /**
   * When false, product create/update only persists in MayorX; enabling and saving again queues export to Profit.
   * Only relevant when sync can export to Profit.
   */
  saveProductChangesToProfit?: boolean;
  /**
   * When false, client create/update only persists in MayorX until this is enabled and the client is saved again.
   */
  saveClientChangesToProfit?: boolean;
  /**
   * When false, salesperson create/update only persists in MayorX until enabled and saved again.
   */
  saveSalesPersonChangesToProfit?: boolean;
  /**
   * Order export defaults (when syncing accepted sales to Profit).
   * Almacén/sucursal in Profit used for ped_num and co_sucu/co_alma (e.g. "01").
   */
  orderExportAlmacen?: string;
  /**
   * dbo.sub_alma.co_sub for default on-hand stock on new products (MayorX → dbo.st_almac).
   * When set, overrides the implicit “first sub for orderExportAlmacen” rule.
   */
  defaultProductStockSubAlmacen?: string;
  /**
   * Map MayorX currency codes to Profit Plus co_mone (e.g. { "USD": "$", "VES": "BSF" }).
   * Used when creating pedidos so moneda/tasa match the order and ERP catalog.
   */
  orderExportCurrencyMapping?: Record<string, string>;
  /** Profit Plus moneda when order currency has no mapping (dbo.moneda.co_mone, e.g. "$" for USD). */
  orderExportDefaultMoneda?: string;
  /** Tasa de cambio when order has no exchangeRate (e.g. 1). */
  orderExportDefaultTasa?: number;
  /**
   * Default IVA % for pedidos when a line's tipo_imp has no dbo.tabulado row (fallback).
   * If omitted, the bridge uses factors.settings.iva (MayorX org default).
   */
  orderExportDefaultIva?: number;
  /**
   * Reference tables imported from Profit in the setup modal (almacen, moneda, condicio, tabulado).
   */
  referenceTables?: ProfitPlus2k8ReferenceTables;
  /** Map MayorX tax id (factors.taxes[].id) to Profit Plus tipo_imp (dbo.tabulado.tipo). */
  taxMapping?: Record<string, string>;
  /**
   * MasterProfit.dbo.employee.employee_i — single ERP user for all MayorX→Profit exports
   * (clientes, artículos, pedidos). Chosen from referenceTables.empleados after reference sync.
   */
  syncEmployeeI?: string;
  /** Optional: last time a full sync was run (for UI/audit). */
  lastSyncAt?: Date | null;
  /** Optional: which entities are included in sync (default all). Omit = all. */
  syncEntities?: Array<'clients' | 'products' | 'sales' | 'salesPeople'>;
  /**
   * True after initial Profit setup step 1 (reference export defaults) is saved on the integration doc.
   * Inventory, Clients, Vendors, and Orders gate ERP/sync UI on this (and on legacy persisted fields when unset).
   */
  initialSetupCompleted?: boolean;
}

/** Reference data from Profit for setup/order-export and product form dropdowns. */
export interface ProfitPlus2k8ReferenceTables {
  almacenes?: Array<{ co_alma?: string; alma_des?: string; [key: string]: unknown }>;
  /** Sub-almacenes (sub_alma). At least one per almacén is required for creating pedidos (reng_ped FK). */
  subAlmacenes?: Array<{ co_alma?: string; co_sub?: string; des_sub?: string; [key: string]: unknown }>;
  monedas?: Array<{ co_mone?: string; mone_des?: string; cambio?: number; [key: string]: unknown }>;
  condiciones?: Array<{ co_cond?: string; cond_des?: string; dias_cred?: number; [key: string]: unknown }>;
  /** dbo.transpor — pedidos.co_tran (transportista). */
  transportistas?: Array<{ co_tran?: string; des_tran?: string; [key: string]: unknown }>;
  tiposImpuesto?: Array<{ tipo?: string; descripcio?: string; porc_vent?: number; [key: string]: unknown }>;
  /** Product masters for Inventory form when Profit Plus is bidirectional (dbo.lin_art, cat_art, sub_lin, colores, proceden, unidades, prov). */
  lineas?: Array<{ co_lin?: string; lin_des?: string; [key: string]: unknown }>;
  categorias?: Array<{ co_cat?: string; cat_des?: string; [key: string]: unknown }>;
  sublineas?: Array<{ co_subl?: string; subl_des?: string; co_lin?: string; [key: string]: unknown }>;
  marcas?: Array<{ co_col?: string; des_col?: string; [key: string]: unknown }>;
  procedencias?: Array<{ cod_proc?: string; des_proc?: string; [key: string]: unknown }>;
  unidades?: Array<{ co_uni?: string; des_uni?: string; [key: string]: unknown }>;
  proveedores?: Array<{ co_prov?: string; prov_des?: string; [key: string]: unknown }>;
  /** dbo.tipo_cli — tipos de cliente (export clientes → Profit). */
  tiposCliente?: Array<{ tip_cli?: string; des_tipo?: string; [key: string]: unknown }>;
  /** dbo.zona */
  zonas?: Array<{ co_zon?: string; zon_des?: string; [key: string]: unknown }>;
  /** dbo.segmento */
  segmentos?: Array<{ co_seg?: string; seg_des?: string; [key: string]: unknown }>;
  /** dbo.vendedor (códigos ERP; distinto del catálogo MayorX de vendedores). */
  vendedoresProfit?: Array<{ co_ven?: string; ven_des?: string; [key: string]: unknown }>;
  /** dbo.cta_ingr */
  cuentasIngreso?: Array<{ co_ingr?: string; descrip?: string; [key: string]: unknown }>;
  /** MasterProfit.dbo.employee — active users (employee_i, last_name) for sync audit fields. */
  empleados?: Array<{ employee_i?: string | number; last_name?: string; [key: string]: unknown }>;
}

export const DEFAULT_PROFIT_PLUS_2K8_ORG_CONFIG: ProfitPlus2k8OrgConfig = {
  enabled: false,
  // Default to bidirectional to minimize user input after enabling.
  syncType: 'bidirectional',
  // Default to `true` to minimize user input when the integration is set to export orders.
  syncAcceptedSalesToProfit: true,
  askBeforeCreatingProfitPedido: true,
  // Default to MayorX-owned stock so enabling Profit does not change inventory behavior.
  stockManagedInMayorX: true,
  saveProductChangesToProfit: true,
  saveClientChangesToProfit: true,
  saveSalesPersonChangesToProfit: true,
  orderExportCurrencyMapping: {},
  lastSyncAt: null,
  syncEntities: ['clients', 'products', 'sales', 'salesPeople'],
};

export interface ConfigurationFactors {
  iva: number;
  availabilityVisible: boolean;
  taxes?: Array<{
    id: string;
    name: string;
    rate: number;
    enabled: boolean;
  }>;
  primaryTaxId?: string;
  paymentMethodsAvailable?: Array<{
    id: number;
    name: string;
    enabled: boolean;
  }>;
  orderApprovalRequired?: boolean;
  allowAdminCurrencyModification?: boolean;
  allowNegativeInventory?: boolean;
  /** When allowNegativeInventory is false: reject create if any line lacks stock. */
  blockSalesWhenInsufficientStock?: boolean;
  /** single = global product.availability (default); multi = per-warehouse balances */
  inventoryMode?: InventoryMode;
  defaultWarehouseId?: string;
  // Currency settings moved from organization
  currency: string;
  useReferenceCurrency?: boolean;
  referenceCurrency?: string;
  exchangeRate: number; // How many local currency units = 1 reference currency unit
  // Automatic exchange rate (optional)
  useAutomaticExchangeRate?: boolean;
  exchangeSourceId?: string;
  /** Nota de entrega: order of blocks and options for print/send */
  deliveryNoteLayout?: DeliveryNoteLayoutConfig;
  /** Profit Plus 2k8 (ERP) integration: enable and choose sync type. */
  profitPlus2k8?: ProfitPlus2k8OrgConfig;
}

/** Block id for delivery note layout (stacked order) */
export type DeliveryNoteBlockId =
  | 'title'
  | 'logo'
  | 'sale_number'
  | 'client'
  | 'salesperson'
  | 'date'
  | 'delivery_address'
  | 'observations'
  | 'products_table'
  | 'totals'
  | 'totals_breakdown'
  | 'exchange_hint'
  | 'fiscal_rif'
  | 'fiscal_taxpayer_type'
  | 'fiscal_withholding_agent'
  | 'fiscal_address'
  | 'fiscal_state'
  | 'fiscal_municipality';

export type DeliveryNoteBlockAlignment = 'left' | 'center' | 'right';

/** Global typography for the delivery note document */
export interface DeliveryNoteTypography {
  /** Font family (CSS value, e.g. "Arial, sans-serif" or "Georgia, serif") */
  fontFamily?: string;
  /** Text color (hex or CSS color) */
  textColor?: string;
  /** Base font size in pixels; applied to body */
  baseFontSizePx?: number;
}

export interface DeliveryNoteLayoutConfig {
  /** Ordered list of block ids (stacked top to bottom) */
  blockOrder: DeliveryNoteBlockId[];
  /** Alignment per block (default left) */
  blockAlignments?: Partial<Record<DeliveryNoteBlockId, DeliveryNoteBlockAlignment>>;
  /** Default: show amounts on print/send */
  defaultShowAmounts?: boolean;
  /** Default: referenceCurrency | organizationCurrency | both */
  defaultAmountCurrencyMode?: 'referenceCurrency' | 'organizationCurrency' | 'both';
  /**
   * When false, the Desc. % column is hidden on the products table (montos siguen usando descuentos).
   * Útil cuando todos los descuentos son 0 para no mostrar una columna confusa.
   */
  defaultShowLineDiscountColumn?: boolean;
  /** When true, title block shows "Nota de entrega #N" instead of "Nota de Entrega" */
  titleIncludesNumber?: boolean;
  /** Global font, color and base size */
  typography?: DeliveryNoteTypography;
  /** Font size in px per block (overrides global base) */
  blockFontSizes?: Partial<Record<DeliveryNoteBlockId, number>>;
  /** Text color per block (overrides global) */
  blockColors?: Partial<Record<DeliveryNoteBlockId, string>>;
}

export const DEFAULT_DELIVERY_NOTE_FONT_FAMILY = 'Arial, Helvetica, sans-serif';
export const DEFAULT_DELIVERY_NOTE_TEXT_COLOR = '#111111';
export const DEFAULT_DELIVERY_NOTE_BASE_FONT_SIZE_PX = 12;

export const DEFAULT_DELIVERY_NOTE_LAYOUT: DeliveryNoteLayoutConfig = {
  blockOrder: [
    'title',
    'logo',
    'client',
    'salesperson',
    'date',
    'delivery_address',
    'observations',
    'products_table',
    // By default show only the total (not the full breakdown),
    // and include RIF when the organization has it configured.
    'totals',
    'exchange_hint',
    'fiscal_rif',
  ],
  defaultShowAmounts: false,
  defaultAmountCurrencyMode: 'referenceCurrency',
  defaultShowLineDiscountColumn: true,
  titleIncludesNumber: true,
};

/** Saved layout default for showing Desc. % (supports legacy `defaultApplyDiscountToAmounts`). */
export function resolveDeliveryNoteShowLineDiscountColumn(
  layout: DeliveryNoteLayoutConfig | undefined | null
): boolean {
  const l = layout as DeliveryNoteLayoutConfig & {
    defaultApplyDiscountToAmounts?: boolean;
  };
  if (typeof l?.defaultShowLineDiscountColumn === 'boolean') {
    return l.defaultShowLineDiscountColumn;
  }
  if (l?.defaultApplyDiscountToAmounts === false) {
    return false;
  }
  return true;
}

export interface Configuration
  extends ConfigurationFactors, BaseEntity, TimestampMixin {
  // Inherits all fields from ConfigurationFactors + BaseEntity + TimestampMixin
}

export interface ConfigurationUpdate extends Partial<ConfigurationFactors> {
  updated?: Date;
}

export interface NotificationFactors {
  emailNotifications: boolean;
  adminEmails: string[];
  saleCreatedNotification: boolean;
  saleApprovedNotification: boolean;
  saleRejectedNotification: boolean;
  clientOrderCreatedNotification: boolean;
  customerPortalRegisteredNotification: boolean;
  returnCreatedNotification: boolean;
  returnApprovedNotification: boolean;
  returnRejectedNotification: boolean;
}

export interface BrandingFactors {
  logo: string;
  logoStoragePath?: string;
  primaryColor: string;
  secondaryColor: string;
  tertiaryColor: string;
  font: string;
}

// Default configuration values
export const CONFIGURATION_DEFAULTS: ConfigurationFactors = {
  iva: 16,
  availabilityVisible: true,
  taxes: [
    { id: 'iva-general', name: 'IVA General', rate: 16, enabled: true },
    { id: 'iva-reducido', name: 'IVA Reducido', rate: 8, enabled: false },
    { id: 'iva-adicional', name: 'IVA Adicional', rate: 31, enabled: false },
    { id: 'igtf', name: 'IGTF', rate: 3, enabled: false },
    { id: 'tax-exempt', name: 'Exento', rate: 0, enabled: true },
  ],
  primaryTaxId: 'iva-general',
  paymentMethodsAvailable: [
    { id: 1, name: 'Efectivo', enabled: true },
    { id: 2, name: 'Crédito', enabled: true },
    { id: 3, name: 'Transferencia bancaria', enabled: true },
    { id: 4, name: 'Cheque', enabled: true },
  ],
  orderApprovalRequired: false,
  allowAdminCurrencyModification: true,
  allowNegativeInventory: false,
  blockSalesWhenInsufficientStock: false,
  inventoryMode: 'single',
  // Currency settings defaults (moved from organization settings)
  currency: 'VES',
  useReferenceCurrency: true,
  referenceCurrency: 'USD',
  exchangeRate: 36.0,
  useAutomaticExchangeRate: false,
  exchangeSourceId: '',
  profitPlus2k8: { ...DEFAULT_PROFIT_PLUS_2K8_ORG_CONFIG },
};

export const DEFAULT_NOTIFICATION_CONFIGURATION: NotificationFactors = {
  emailNotifications: true,
  adminEmails: [],
  saleCreatedNotification: true,
  saleApprovedNotification: true,
  saleRejectedNotification: true,
  clientOrderCreatedNotification: true,
  customerPortalRegisteredNotification: true,
  returnCreatedNotification: true,
  returnApprovedNotification: true,
  returnRejectedNotification: true,
};

export const DEFAULT_BRANDING_CONFIGURATION: BrandingFactors = {
  logo: '',
  // Match app's blackish theme from src/index.css
  primaryColor: '#1a1a1a',
  secondaryColor: '#6c757d',
  tertiaryColor: '#2d2d2d',
  font: 'Inter, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif',
};
